import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import PageContainer from '../components/common/PageContainer';
import ProductCard from '../components/common/ProductCard';
import storeApi from '../services/storeApi';

/**
 * ShopPage - Shop listing page with filters
 * Follows React Rules: Side effects in useEffect, proper state management
 */
const ShopPage = () => {
  const { category: categorySlug } = useParams();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]); // For hierarchical display
  const [manufacturers, setManufacturers] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedManufacturers, setSelectedManufacturers] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    in_stock: searchParams.get('in_stock') || '',
    manufacturer: searchParams.get('manufacturer') || '',
    ordering: searchParams.get('ordering') || '-created_at'
  });
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    page: 1,
    page_size: 12
  });
  const [error, setError] = useState(null);

  // Fetch categories (parent categories for main menu)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await storeApi.getParentCategories();
        setCategories(Array.isArray(data) ? data : (data.results || []));
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  // Fetch all categories (for filter checkboxes)
  useEffect(() => {
    const fetchAllCategories = async () => {
      try {
        const data = await storeApi.getCategories();
        setAllCategories(Array.isArray(data) ? data : (data.results || []));
      } catch (err) {
        console.error('Error fetching all categories:', err);
      }
    };
    fetchAllCategories();
  }, []);

  // Fetch manufacturers
  useEffect(() => {
    const fetchManufacturers = async () => {
      try {
        const data = await storeApi.getManufacturers();
        setManufacturers(Array.isArray(data) ? data : (data.results || []));
      } catch (err) {
        console.error('Error fetching manufacturers:', err);
      }
    };
    fetchManufacturers();
  }, []);

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = {
          ...filters,
          page: pagination.page,
          page_size: pagination.page_size
        };

        // Add category filter if category slug is provided
        if (categorySlug) {
          // Find category by slug
          const category = categories.find(cat => cat.slug === categorySlug);
          if (category) {
            params.category = category.id;
            setSelectedCategory(category);
          }
        }

        // Remove empty filters
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null) {
            delete params[key];
          }
        });

        const response = await storeApi.getProducts(params);
        
        // Handle paginated response
        if (response.results) {
          setProducts(response.results);
          setPagination(prev => ({
            ...prev,
            count: response.count,
            next: response.next,
            previous: response.previous
          }));
        } else {
          setProducts(Array.isArray(response) ? response : []);
        }
        
        // Clear error if request succeeded (even with empty results)
        setError(null);
      } catch (err) {
        console.error('Error fetching products:', err);
        // Only show error for real API/network errors (not empty results)
        const status = err.response?.status;
        if (status && status >= 400 && status !== 404) {
          setError('Failed to load products. Please try again.');
        } else {
          // For 404 or network errors, treat as no products
          setError(null);
        }
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categorySlug, filters, pagination.page, pagination.page_size, categories]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddToCart = async (productId) => {
    try {
      await storeApi.addToCart(productId, 1);
      // Trigger cart update event for header
      window.dispatchEvent(new Event('cartUpdated'));
      alert('Product added to cart!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (error.response?.status === 401) {
        alert('Please login to add items to cart');
      } else {
        alert('Failed to add product to cart. Please try again.');
      }
    }
  };

  const handleAddToWishlist = async (productId) => {
    try {
      await storeApi.addToWishlist(productId);
      alert('Product added to wishlist!');
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      alert('Please login to add items to wishlist');
    }
  };

  const handleQuickView = (productId) => {
    console.log('Quick view:', productId);
  };

  return (
    <PageContainer>
      <div className="shop-area pt-60 pb-100">
        <div className="container">
          <div className="row">
            {/* Sidebar Filters */}
            <div className="col-lg-3 order-2 order-lg-1">
              {/* Category Menu with Subcategories */}
              {categories.length > 0 && (
                <div className="sidebar-categores-box mt-sm-30 mt-xs-30">
                  <div className="sidebar-title">
                    <h2>{selectedCategory?.name || categories[0]?.name || 'Categories'}</h2>
                  </div>
                  <div className="category-sub-menu">
                    <ul>
                      {categories.map(cat => {
                        // Get child categories for this parent
                        // Handle both parent as ID or object
                        const childCategories = allCategories.filter(c => {
                          const parentId = typeof c.parent === 'object' ? c.parent?.id : c.parent;
                          return parentId === cat.id;
                        });
                        return (
                          <li key={cat.id} className={childCategories.length > 0 ? 'has-sub' : ''}>
                            <a 
                              href={`/store/shop/${cat.slug}`}
                              onClick={(e) => {
                                e.preventDefault();
                                setSelectedCategory(cat);
                                handleFilterChange('category', cat.id);
                              }}
                              className={selectedCategory?.id === cat.id ? 'active' : ''}
                            >
                              {cat.name}
                            </a>
                            {childCategories.length > 0 && (
                              <ul>
                                {childCategories.map(child => (
                                  <li key={child.id}>
                                    <a 
                                      href={`/store/shop/${child.slug}`}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setSelectedCategory(child);
                                        handleFilterChange('category', child.id);
                                      }}
                                    >
                                      {child.name}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              )}

              {/* Filter By Section */}
              <div className="sidebar-categores-box">
                <div className="sidebar-title">
                  <h2>Filter By</h2>
                </div>
                <button 
                  className="btn-clear-all mb-sm-30 mb-xs-30"
                  onClick={() => {
                    setFilters({
                      search: '',
                      min_price: '',
                      max_price: '',
                      in_stock: '',
                      manufacturer: '',
                      ordering: '-created_at'
                    });
                    setSelectedManufacturers([]);
                    setSelectedCategories([]);
                    setSelectedCategory(null);
                  }}
                >
                  Clear all
                </button>

                {/* Brand Filter */}
                {manufacturers.length > 0 && (
                  <div className="filter-sub-area">
                    <h5 className="filter-sub-titel">Brand</h5>
                    <div className="categori-checkbox">
                      <form action="#">
                        <ul>
                          {manufacturers.map(manufacturer => (
                            <li key={manufacturer.id}>
                              <input 
                                type="checkbox" 
                                name="product-brand"
                                checked={selectedManufacturers.includes(manufacturer.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedManufacturers([...selectedManufacturers, manufacturer.id]);
                                    handleFilterChange('manufacturer', manufacturer.id);
                                  } else {
                                    const updated = selectedManufacturers.filter(id => id !== manufacturer.id);
                                    setSelectedManufacturers(updated);
                                    handleFilterChange('manufacturer', updated.length > 0 ? updated[0] : '');
                                  }
                                }}
                              />
                              <a href="#" onClick={(e) => e.preventDefault()}>
                                {manufacturer.name} ({manufacturer.product_count || 0})
                              </a>
                            </li>
                          ))}
                        </ul>
                      </form>
                    </div>
                  </div>
                )}

                {/* Categories Filter */}
                {allCategories.length > 0 && (
                  <div className="filter-sub-area pt-sm-10 pt-xs-10">
                    <h5 className="filter-sub-titel">Categories</h5>
                    <div className="categori-checkbox">
                      <form action="#">
                        <ul>
                          {allCategories.slice(0, 10).map(cat => (
                            <li key={cat.id}>
                              <input 
                                type="checkbox" 
                                name="product-categori"
                                checked={selectedCategories.includes(cat.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedCategories([...selectedCategories, cat.id]);
                                  } else {
                                    setSelectedCategories(selectedCategories.filter(id => id !== cat.id));
                                  }
                                }}
                              />
                              <a href="#" onClick={(e) => e.preventDefault()}>
                                {cat.name} ({cat.product_count || 0})
                              </a>
                            </li>
                          ))}
                        </ul>
                      </form>
                    </div>
                  </div>
                )}

                {/* Price Range Filter */}
                <div className="filter-sub-area pt-sm-10 pt-xs-10">
                  <h5 className="filter-sub-titel">Price</h5>
                  <div className="price-filter">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.min_price}
                      onChange={(e) => handleFilterChange('min_price', e.target.value)}
                      style={{ width: '45%', marginRight: '5%' }}
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.max_price}
                      onChange={(e) => handleFilterChange('max_price', e.target.value)}
                      style={{ width: '45%' }}
                    />
                  </div>
                </div>

                {/* Availability Filter */}
                <div className="filter-sub-area pt-sm-10 pt-xs-10">
                  <h5 className="filter-sub-titel">Availability</h5>
                  <div className="categori-checkbox">
                    <form action="#">
                      <ul>
                        <li>
                          <input
                            type="checkbox"
                            name="product-availability"
                            checked={filters.in_stock === 'true'}
                            onChange={(e) => handleFilterChange('in_stock', e.target.checked ? 'true' : '')}
                          />
                          <a href="#" onClick={(e) => e.preventDefault()}>In Stock</a>
                        </li>
                      </ul>
                    </form>
                  </div>
                </div>
              </div>

              {/* Tags Section */}
              {categories.length > 0 && (
                <div className="sidebar-categores-box mb-sm-0 mb-xs-0">
                  <div className="sidebar-title">
                    <h2>Tags</h2>
                  </div>
                  <div className="category-tags">
                    <ul>
                      {categories.slice(0, 6).map(cat => (
                        <li key={cat.id}>
                          <a 
                            href={`/store/shop/${cat.slug}`}
                            onClick={(e) => {
                              e.preventDefault();
                              setSelectedCategory(cat);
                              handleFilterChange('category', cat.id);
                            }}
                          >
                            {cat.name}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Products Grid */}
            <div className="col-lg-9 order-1 order-lg-2">
              <div className="shop-top-bar">
                <div className="select-shoing-wrap">
                  <p>
                    Showing {products.length} of {pagination.count} products
                    {selectedCategory && ` in ${selectedCategory.name}`}
                  </p>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <p>Loading products...</p>
                </div>
              ) : error ? (
                <div className="text-center py-5">
                  <p className="text-danger">{error}</p>
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-5">
                  <p>There are no products yet. Please check back later.</p>
                </div>
              ) : (
                <>
                  <div className="shop-products-wrap">
                    <div className="row">
                      {products.map((product) => (
                        <div key={product.id} className="col-lg-4 col-md-6 col-sm-6 mt-40">
                          <ProductCard
                            product={product}
                            onAddToCart={handleAddToCart}
                            onAddToWishlist={handleAddToWishlist}
                            onQuickView={handleQuickView}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Pagination */}
                  {pagination.count > pagination.page_size && (
                    <div className="pagination-area">
                      <ul className="pagination">
                        {pagination.previous && (
                          <li>
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handlePageChange(pagination.page - 1);
                              }}
                            >
                              Previous
                            </a>
                          </li>
                        )}
                        <li className="active">
                          <span>Page {pagination.page}</span>
                        </li>
                        {pagination.next && (
                          <li>
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handlePageChange(pagination.page + 1);
                              }}
                            >
                              Next
                            </a>
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default ShopPage;
